import { Box, Collapse, Flex, Icon, Link, useDisclosure } from '@chakra-ui/react';
import { ArrowTopRightOnSquareIcon, Cog6ToothIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '@main/complykit/components/button';
import { Card } from '@main/complykit/components/card';
import { IconButton } from '@main/complykit/components/icon-button';
import { Content } from '@main/complykit/components/slots';
import {
  Integration_Names_Enum,
  Policy_Integration_Error_Statuses_Enum,
} from '@main/graphql/types.generated';
import type { PolicyIntegrationLinkedFile } from '@main/integrations';
import { useAlertDialog } from '@main/ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IntegrationDetails } from '../integration-details';
import { IntegrationLogo } from '../integration-logo';
import { PolicyConfigRef, PolicyIntegrationConfig } from '../policy-integration-config';
import { PolicyIntegrationRetryAlert } from '../policy-integration-retry-alert';

export type PolicyIntegrationCardProps = {
  providerName: Integration_Names_Enum;
  connectorName?: string;
  orgIntegrationId: string;
  linkedFile?: PolicyIntegrationLinkedFile | null;
  errorStatus?: Policy_Integration_Error_Statuses_Enum | null;
  isReadOnly?: boolean;
  onSave: (config: unknown) => Promise<void>;
  onUnlink: () => Promise<unknown>;
  onRetry: () => Promise<unknown>;
};

export const PolicyIntegrationCard = ({
  providerName,
  connectorName,
  orgIntegrationId,
  linkedFile,
  errorStatus,
  isReadOnly = false,
  onSave,
  onUnlink,
  onRetry,
}: PolicyIntegrationCardProps) => {
  const { t } = useTranslation('integrations');
  const { openDialog } = useAlertDialog();
  const configRef = useRef<PolicyConfigRef>(null);
  const configCollapse = useDisclosure();
  const isLinked = !!linkedFile;
  const fileUrl = linkedFile ? decodeURIComponent(linkedFile.url) : undefined;
  const fileLabel = linkedFile?.path ?? linkedFile?.name;
  const [isSaving, setIsSaving] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const handleSaveClick = async () => {
    setIsSaving(true);
    try {
      const resolved = await configRef.current?.submit();
      if (!resolved) return;
      await onSave(resolved);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnlinkClick = () => {
    if (isLinked) {
      void openDialog({
        dialogHeader: t('policyIntegrationCard.unlink.header'),
        dialogContent: t('policyIntegrationCard.unlink.content'),
        confirmAction: {
          children: t('policyIntegrationCard.unlink.confirm'),
          onClick: () => onUnlink(),
        },
      });
    } else {
      void onUnlink();
    }
  };

  const handleRetryClick = async () => {
    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <Card>
      <Content>
        <Flex gap={4}>
          <IntegrationLogo name={providerName} />

          <Box flexGrow={1}>
            <IntegrationDetails
              providerName={providerName}
              connectorName={connectorName}
              helpMessage={
                <Link href={fileUrl} wordBreak="break-all" isExternal>
                  {fileLabel} <Icon as={ArrowTopRightOnSquareIcon} />
                </Link>
              }
              isActive={isLinked}
            />
          </Box>

          <Flex gap={2} alignItems="start">
            <IconButton
              variant="outline"
              icon={Cog6ToothIcon}
              label={t('configureForm.configureBtn')}
              onPress={configCollapse.onToggle}
            />
            <IconButton
              variant="outline"
              tone="critical"
              icon={TrashIcon}
              label={t('policyIntegrationCard.unlink.action')}
              isDisabled={isSaving || isReadOnly}
              onPress={handleUnlinkClick}
            />
          </Flex>
        </Flex>

        {errorStatus && (
          <Box mt={4}>
            <PolicyIntegrationRetryAlert
              status={errorStatus}
              isLoading={isRetrying}
              isDisabled={isReadOnly}
              onRetry={handleRetryClick}
            />
          </Box>
        )}

        <Collapse in={configCollapse.isOpen}>
          <Box mt={8} mb={4} mx={0.5}>
            <PolicyIntegrationConfig
              ref={configRef}
              providerName={providerName}
              orgIntegrationId={orgIntegrationId}
              isDisabled={isReadOnly}
            />
          </Box>

          <Flex gap={2} w="full" justifyContent="end">
            <Button variant="outline" isDisabled={isSaving} onPress={configCollapse.onClose}>
              {t('configureForm.cancelBtn')}
            </Button>
            <Button
              isDisabled={isReadOnly}
              isPending={isSaving}
              onPress={() => void handleSaveClick()}
            >
              {t('configureForm.saveBtn')}
            </Button>
          </Flex>
        </Collapse>
      </Content>
    </Card>
  );
};
